/**
 * Hook for checking duplicate spots based on placeId
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import { ApiError, type DuplicateCheckResponseDto } from "@tabipla/shared";

interface UseDuplicateCheckOptions {
  placeId: string | null;
  tripId?: string;
  enabled?: boolean;
}

/**
 * Check if a place already exists in user's collections
 */
export function useDuplicateCheck({ placeId, tripId, enabled = true }: UseDuplicateCheckOptions) {
  return useQuery({
    queryKey: ["duplicate-check", placeId, tripId],
    queryFn: async () => {
      if (!placeId) {
        return null;
      }

      const params = new URLSearchParams({ placeId });
      if (tripId) {
        params.append("tripId", tripId);
      }

      const response = await apiClient.get<DuplicateCheckResponseDto>(
        `/api/integration/check-duplicate?${params.toString()}`
      );

      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to check duplicate");
      }

      return response.data ?? null;
    },
    enabled: enabled && !!placeId,
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Helper to check if any duplicate exists
 */
export function hasDuplicate(data: DuplicateCheckResponseDto | null | undefined): boolean {
  if (!data) return false;
  return data.existsInTrip || data.existsInMekke || data.existsInFavorites;
}

/**
 * Get a human-readable duplicate message
 */
export function getDuplicateMessage(
  data: DuplicateCheckResponseDto,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const messages: string[] = [];

  if (data.existsInTrip && data.tripSpot) {
    messages.push(t("spots.duplicateInTrip", { day: data.tripSpot.dayNumber }));
  }

  if (data.existsInMekke && data.mekkeSpot) {
    messages.push(
      t("spots.duplicateInMekke", {
        status: data.mekkeSpot.status === "visited" ? t("mekke.visited") : t("mekke.wantToGo"),
      })
    );
  }

  if (data.existsInFavorites) {
    messages.push(t("spots.duplicateInFavorites"));
  }

  return messages.join(", ");
}
